import React, {useCallback, useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {toast} from "react-toastify";
import {Row, Col} from "reactstrap";
import SafeView from "../../../components/safeView/SafeView";
import ProtectedContent from "../../../components/protectedContent";
import Button from "../../../components/Button";
import {useCleanupOnUnload} from "../../../hooks/useCleanupOnUnload";
import {
  storeReportSites,
  storeReportLocations,
  storeReportControllers,
  storeReportSelectedControllerList,
  createReport,
  storeCreateReportData,
  requestEachReport,
  changeReportStatus,
} from "../../../redux/Actions/reports.actions";
import {
  getSelectedControllerList,
  getCreateReportData,
} from "../../../redux/Selectors/reports.selectors";
import AddComment from "./addComment";
import AddControllerForm from "./addControllerForm";
import ControllerList from "./controllerList";
import "./report.scss";

const CreateReport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {reportId} = useParams();
  const controllerList = useSelector(getSelectedControllerList);
  const createReportData = useSelector(getCreateReportData);
  const {name = "", status} = createReportData;

  useEffect(() => {
    if (reportId) {
      dispatch(requestEachReport(reportId));
    }
  }, [dispatch, reportId]);

  const clearData = useCallback(() => {
    dispatch(storeCreateReportData({}));
    dispatch(storeReportSites([]));
    dispatch(storeReportLocations([]));
    dispatch(storeReportControllers([]));
    dispatch(storeReportSelectedControllerList([]));
  }, [dispatch]);

  useCleanupOnUnload(clearData);

  const handleCancel = () => {
    clearData();
    navigate("/app/reports", {
      state: {activeTab: "2"},
    });
  };

  const handleCreateReport = () => {
    if (name) {
      dispatch(
        createReport({id: reportId, name, inventories: controllerList}, handleCancel)
      );
    } else {
      toast.error("Name is required");
    }
  };

  const handleStatusChange = (status) => {
    dispatch(changeReportStatus(reportId, status, handleCancel));
  };

  return (
    <SafeView>
      <Row style={{marginTop: "1rem", marginBottom: "1rem"}}>
        <Col lg={6}>
          <div style={{marginBottom: "1rem"}}>
            <AddControllerForm />
          </div>
          {controllerList?.length > 0 && (
            <div style={{marginBottom: "1rem"}}>
              <ControllerList />
            </div>
          )}
        </Col>
        <Col lg={6}>
          {controllerList?.length > 0 && controllerList[0]?.comments?.length > 0 && (
            <div style={{marginBottom: "1rem"}}>
              <AddComment />
            </div>
          )}
          <div className="d-flex justify-content-end">
            <Button
              withRadius
              type={"info-small"}
              onClick={handleCancel}
              data-cy="cancel-btn"
            >
              CANCEL
            </Button>
            {status === "draft" && (
              <ProtectedContent perms={["change_report"]}>
                <Button
                  withRadius
                  type={"info-small"}
                  onClick={() => handleStatusChange("under-review")}
                  data-cy="review-btn"
                >
                  REVIEW
                </Button>
              </ProtectedContent>
            )}
            {status === "under-review" && (
              <ProtectedContent perms={["change_report"]}>
                <Button
                  withRadius
                  type={"info-small"}
                  onClick={() => handleStatusChange("draft")}
                  data-cy="draft-btn"
                >
                  DRAFT
                </Button>
              </ProtectedContent>
            )}
            {status === "under-review" || status === "submitted" ? (
              <>
                <Button
                  withRadius
                  onClick={() => navigate(`/app/reports/${reportId}/view-charts`)}
                  type="primary"
                >
                  VIEW CHARTS
                </Button>
                {status === "under-review" && (
                  <ProtectedContent perms={["change_report"]}>
                    <Button
                      withRadius
                      onClick={() => handleStatusChange("submitted")}
                      data-cy="finalize-btn"
                    >
                      FINALIZE REPORT
                    </Button>
                  </ProtectedContent>
                )}
              </>
            ) : (
              <ProtectedContent perms={["change_report"]}>
                <Button withRadius onClick={handleCreateReport} data-cy="submit-btn">
                  SAVE
                </Button>
              </ProtectedContent>
            )}
          </div>
        </Col>
      </Row>
    </SafeView>
  );
};

export default CreateReport;
